/**
 * Сервис для управления пуш-уведомлениями в Worker App
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || ''
const PUSH_PERMISSION_KEY = 'qrium_push_permission'
const PUSH_SUBSCRIPTION_KEY = 'qrium_push_subscribed'

export class PushNotificationService {
  /**
   * Получить сохраненное состояние разрешения
   */
  static getSavedPermission(): NotificationPermission | null {
    const saved = localStorage.getItem(PUSH_PERMISSION_KEY)
    return saved as NotificationPermission | null
  }

  /**
   * Сохранить состояние разрешения
   */
  static savePermission(permission: NotificationPermission): void {
    localStorage.setItem(PUSH_PERMISSION_KEY, permission)
    console.log('[PushService] Permission saved:', permission)
  }

  /**
   * Проверить, подписан ли пользователь (из localStorage)
   */
  static isSubscribedLocally(): boolean {
    return localStorage.getItem(PUSH_SUBSCRIPTION_KEY) === 'true'
  }

  /**
   * Сохранить состояние подписки
   */
  static saveSubscriptionState(subscribed: boolean): void {
    localStorage.setItem(PUSH_SUBSCRIPTION_KEY, subscribed ? 'true' : 'false')
    console.log('[PushService] Subscription state saved:', subscribed)
  }

  /**
   * Очистить сохраненные данные (при сбросе приложения)
   */
  static clearSavedData(): void {
    localStorage.removeItem(PUSH_PERMISSION_KEY)
    localStorage.removeItem(PUSH_SUBSCRIPTION_KEY)
    console.log('[PushService] Saved data cleared')
  }
  /**
   * Запросить разрешение на уведомления
   */
  static async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      throw new Error('Уведомления не поддерживаются этим браузером')
    }

    const permission = await Notification.requestPermission()
    console.log('[PushService] Permission:', permission)
    
    // Сохраняем разрешение в localStorage
    this.savePermission(permission)
    
    return permission
  }

  /**
   * Проверить поддержку пуш-уведомлений
   */
  static isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    )
  }

  /**
   * Подписаться на пуш-уведомления
   */
  static async subscribe(employeeId: string, forceRequest = false): Promise<void> {
    console.log('[PushService] 📬 Subscribing for employee:', employeeId, 'forceRequest:', forceRequest)

    if (!this.isSupported()) {
      console.error('[PushService] ❌ Push notifications not supported in this browser')
      throw new Error('Пуш-уведомления не поддерживаются')
    }

    if (!VAPID_PUBLIC_KEY) {
      console.error('[PushService] ❌ VAPID public key not configured')
      console.error('[PushService] Check VITE_VAPID_PUBLIC_KEY environment variable')
      throw new Error('VAPID ключ не настроен')
    }
    
    console.log('[PushService] ✅ VAPID public key configured:', VAPID_PUBLIC_KEY.substring(0, 20) + '...')

    // Проверяем разрешение
    let permission = Notification.permission
    console.log('[PushService] Current permission:', permission)
    
    if (permission === 'default' || forceRequest) {
      console.log('[PushService] Requesting permission...')
      permission = await this.requestPermission()
    } else {
      // Сохраняем текущее состояние
      this.savePermission(permission)
    }

    if (permission !== 'granted') {
      console.warn('[PushService] Permission denied or dismissed')
      throw new Error('Разрешение на уведомления не предоставлено')
    }

    // Ждём готовности Service Worker
    const registration = await navigator.serviceWorker.ready
    console.log('[PushService] ✅ Service Worker ready')

    // Проверяем существующую подписку
    let subscription = await registration.pushManager.getSubscription()
    
    if (subscription) {
      console.log('[PushService] 🔄 Existing subscription found, unsubscribing...')
      await subscription.unsubscribe()
    }

    // Создаём новую подписку
    console.log('[PushService] 📝 Creating new subscription...')
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as any,
      })
      console.log('[PushService] ✅ Subscription created successfully')
      console.log('[PushService] Endpoint:', subscription.endpoint)
    } catch (error) {
      console.error('[PushService] ❌ Failed to create subscription:', error)
      throw error
    }

    // Получаем ключи
    const p256dhKey = subscription.getKey('p256dh')
    const authKey = subscription.getKey('auth')

    if (!p256dhKey || !authKey) {
      throw new Error('Не удалось получить ключи подписки')
    }

    // Конвертируем в base64
    const p256dhKeyBase64 = this.arrayBufferToBase64(p256dhKey)
    const authKeyBase64 = this.arrayBufferToBase64(authKey)

    // Отправляем подписку на backend
    console.log('[PushService] Sending subscription to backend...')
    
    // Проверяем что API_BASE_URL установлен
    if (!API_BASE_URL) {
      console.error('[PushService] ❌ API_BASE_URL not configured')
      console.error('[PushService] Check VITE_API_BASE_URL environment variable')
      throw new Error('API URL не настроен')
    }
    
    // Формируем правильный URL: API_BASE_URL уже содержит https://api.recvi.ru
    const subscribeUrl = `${API_BASE_URL}/api/v1/worker/push/subscribe`
    console.log('[PushService] Subscribe URL:', subscribeUrl)
    
    const payload = {
      employee_id: employeeId,
      endpoint: subscription.endpoint,
      p256dh_key: p256dhKeyBase64,
      auth_key: authKeyBase64,
      user_agent: navigator.userAgent,
    }
    
    console.log('[PushService] 🔍 Payload details:')
    console.log('[PushService]   employee_id:', employeeId)
    console.log('[PushService]   employee_id type:', typeof employeeId)
    console.log('[PushService]   employee_id length:', employeeId?.length)
    console.log('[PushService]   endpoint:', subscription.endpoint.substring(0, 50) + '...')
    
    try {
      const response = await fetch(subscribeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const error = await response.text()
        console.error('[PushService] ❌ Backend error:', error)
        throw new Error(`Ошибка подписки: ${error}`)
      }

      const result = await response.json()
      console.log('[PushService] ✅ Successfully subscribed:', result)
      
      // Сохраняем состояние подписки
      this.saveSubscriptionState(true)
    } catch (error) {
      console.error('[PushService] ❌ Network error during subscription:', error)
      throw error
    }
  }

  /**
   * Отписаться от пуш-уведомлений
   */
  static async unsubscribe(): Promise<void> {
    console.log('[PushService] Unsubscribing...')

    if (!this.isSupported()) {
      return
    }

    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()

    if (!subscription) {
      console.log('[PushService] No subscription found')
      return
    }

    // Отправляем на backend
    if (API_BASE_URL) {
      try {
        await fetch(`${API_BASE_URL}/api/v1/worker/push/unsubscribe`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            endpoint: subscription.endpoint,
          }),
        })
      } catch (error) {
        console.error('[PushService] Error notifying backend:', error)
      }
    }

    // Удаляем локально
    await subscription.unsubscribe()
    
    // Очищаем состояние подписки
    this.saveSubscriptionState(false)
    
    console.log('[PushService] ✅ Successfully unsubscribed')
  }

  /**
   * Получить текущую подписку
   */
  static async getSubscription(): Promise<PushSubscription | null> {
    if (!this.isSupported()) {
      return null
    }

    const registration = await navigator.serviceWorker.ready
    return await registration.pushManager.getSubscription()
  }

  /**
   * Проверить подписан ли пользователь
   */
  static async isSubscribed(): Promise<boolean> {
    const subscription = await this.getSubscription()
    return subscription !== null
  }

  /**
   * Конвертировать URL-safe base64 в Uint8Array
   */
  private static urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }

    return outputArray
  }

  /**
   * Конвертировать ArrayBuffer в base64
   */
  private static arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return window.btoa(binary)
  }
}
